// Imports
import GLightbox from "glightbox";

// ------------------------------------------------------------------
// OPEN EXTERNAL LINKS AND PDFs IN NEW TABS
// ------------------------------------------------------------------

const openExternalLinks = () => {
  const pdfLinks = document.querySelectorAll("a[href$='.pdf']");

  pdfLinks.forEach((link) => {
    link.setAttribute("target", "_blank");
  });

  const externalLinks = document.querySelectorAll("a");

  externalLinks.forEach((link) => {
    if (link.hostname !== window.location.hostname) {
      link.setAttribute("target", "_blank");
    }
  });
};

// ------------------------------------------------------------------
// SET HTML ATTRIBUTES FOR DECORATIVE ELEMENTS
// ------------------------------------------------------------------
const updateDecorativeElements = () => {
  const decorativeElements = document.querySelectorAll(".js-decorative");

  if (!decorativeElements) return;

  decorativeElements.forEach((element) => {
    element.setAttribute("role", "presentation");
    element.setAttribute("aria-hidden", "true");
    element.setAttribute("tabindex", "-1");
    element.style.pointerEvents = "none";
  });
};

// -----------------------------------------------------------------------------
// SCROLL TO TOP
// -----------------------------------------------------------------------------
const scrollToPageTop = () => {
  const backToTop = document.querySelector(".cd-top");

  if (!backToTop) return;

  const offset = 200;
  const scrollY = window.scrollY;

  backToTop.style.display = scrollY > offset ? "block" : "none";

  setTimeout(() => {
    backToTop.classList.toggle("cd-is-visible", scrollY > offset);
  }, 10);

  backToTop.addEventListener("click", () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  });
};

// ------------------------------------------------------------------
// DISCLAIMER POPUP
// ------------------------------------------------------------------

const disclaimerPopup = () => {
  const disclaimer = document.querySelector("#disclaimer-popup");
  const body = document.querySelector("body");
  if (!disclaimer) return;

  const closeButton = document.querySelector("#disclaimer-popup .popup-accept");
  const popupCookieName = "proactive-disclaimer-popup";

  const cookieValue = document.cookie.split("; ").find((row) => row.startsWith(popupCookieName));
  const isPopupClosed = cookieValue ? cookieValue.split("=")[1] === "true" : false;

  let previouslyFocused = null;
  let focusableElements = null;
  let firstFocusable = null;
  let lastFocusable = null;
  let externalFocusableElements = [];

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      // Match decline button behavior - navigate to home
      window.location.href = "/";
      return;
    }

    // Focus trap
    const isTabPressed = e.key === "Tab" || e.keyCode === 9;
    if (!isTabPressed) return;

    if (e.shiftKey) {
      if (document.activeElement === firstFocusable) {
        lastFocusable.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastFocusable) {
        firstFocusable.focus();
        e.preventDefault();
      }
    }
  };

  const openPopup = () => {
    previouslyFocused = document.activeElement;

    // Make all external elements unfocusable
    const allFocusableElements = document.querySelectorAll(
      'a[href]:not(#disclaimer-popup a[href]), button:not(#disclaimer-popup button), input:not(#disclaimer-popup input), select:not(#disclaimer-popup select), textarea:not(#disclaimer-popup textarea), [tabindex]:not(#disclaimer-popup [tabindex]):not([tabindex="-1"])'
    );

    allFocusableElements.forEach((element) => {
      // Store original tabindex
      if (element.hasAttribute("tabindex")) {
        externalFocusableElements.push({
          element: element,
          tabindex: element.getAttribute("tabindex")
        });
      } else {
        externalFocusableElements.push({
          element: element,
          tabindex: null
        });
      }
      // Make unfocusable
      element.setAttribute("tabindex", "-1");
    });

    disclaimer.setAttribute("aria-hidden", "false");
    disclaimer.style.display = "block";
    body.style.overflow = "hidden";

    // Get focusable elements within popup
    focusableElements = disclaimer.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable = focusableElements[0];
    lastFocusable = focusableElements[focusableElements.length - 1];

    // Focus specifically on the accept button
    const acceptButton = closeButton;
    if (acceptButton) {
      acceptButton.focus();
    }

    // Add event listeners
    document.addEventListener("keydown", handleKeyDown);
  };

  const closePopup = () => {
    // Restore focus first to avoid aria-hidden warning
    if (previouslyFocused) {
      previouslyFocused.focus();
    }

    // Now safely hide the popup
    disclaimer.setAttribute("aria-hidden", "true");
    disclaimer.style.display = "none";
    body.style.overflow = "auto";

    // Then restore focusability to external elements
    externalFocusableElements.forEach(({ element, tabindex }) => {
      if (tabindex === null) {
        element.removeAttribute("tabindex");
      } else {
        element.setAttribute("tabindex", tabindex);
      }
    });
    // Clear the array
    externalFocusableElements.length = 0;

    // Set cookie and remove event listeners
    document.cookie = `${popupCookieName}=true; max-age=${14 * 24 * 60 * 60}; path=/`;
    document.removeEventListener("keydown", handleKeyDown);
  };

  if (!isPopupClosed) {
    openPopup();
  }

  closeButton.addEventListener("click", closePopup);
};

// ------------------------------------------------------------------
// BLOCK STRETCH DYNAMICALLY
// ------------------------------------------------------------------
const alignFull = () => {
  const viewportFullWidth = document.querySelectorAll(".js-align-full");

  if (!viewportFullWidth) return;

  viewportFullWidth.forEach((item) => {
    var rect = item.parentElement.getBoundingClientRect();
    item.style.marginLeft = -rect.left + "px";
    item.style.width = document.body.clientWidth + "px";
  });
};

const alignItemsRight = () => {
  const viewportWidthRight = document.querySelectorAll(".js-align-right");

  if (!viewportWidthRight) return;

  viewportWidthRight.forEach((item) => {
    const rect = item.parentElement.getBoundingClientRect();
    const bodyWidth = document.body.clientWidth;
    item.style.width = rect.width + (bodyWidth - rect.right) + "px";
  });
};

// ------------------------------------------------------------------
// ACTIVATE LIGHTBOX
// ------------------------------------------------------------------
const lightbox = () => {
  const lightbox = GLightbox({
    selector: ".wp-block-gallery .wp-block-image a",
    touchNavigation: true,
    loop: true,
    autoplayVideos: true
  });
};

export { openExternalLinks, scrollToPageTop, lightbox, disclaimerPopup };
